import LogRocket from 'logrocket';

export interface LogRocketUser {
  id: string;
  name: string;
  email: string;
}

export type TrackEventProperties = Record<
  string,
  string | number | boolean | null | undefined | string[] | number[] | boolean[]
>;

/**
 * Initialize LogRocket with error handling
 */
export const initializeLogRocket = (): void => {
  const appId = import.meta.env.VITE_LOGROCKET_APP_ID;
  const serverUrl = import.meta.env.VITE_LOGROCKET_SERVER_URL;

  if (!appId) {
    console.warn('LogRocket: VITE_LOGROCKET_APP_ID is not set, skipping initialization');
    return;
  }

  if (!serverUrl) {
    console.warn('LogRocket: VITE_LOGROCKET_SERVER_URL is not set, skipping initialization');
    return;
  }

  try {
    LogRocket.init(
      appId,
      {
        serverURL: serverUrl,
      }
    );
    console.log('LogRocket initialized successfully');
  } catch (error) {
    console.error('Failed to initialize LogRocket:', error);
  }
};

/**
 * Identify user in LogRocket
 */
export const identifyUser = (user: LogRocketUser): void => {
  try {
    LogRocket.identify(user.id, {
      name: user.name,
      email: user.email,
    });
    console.log('User identified in LogRocket:', user.email);
  } catch (error) {
    console.error('Failed to identify user in LogRocket:', error);
  }
};

/**
 * Track custom events in LogRocket
 */
export const trackEvent = (
  eventName: string,
  properties?: TrackEventProperties
): void => {
  try {
    LogRocket.track(eventName, properties);
  } catch (error) {
    console.error('Failed to track event in LogRocket:', error);
  }
};

/**
 * Capture exceptions in LogRocket
 */
export const captureException = (
  error: Error,
  context?: Record<string, unknown>
): void => {
  try {
    LogRocket.captureException(error, context);
  } catch (logRocketError) {
    console.error('Failed to capture exception in LogRocket:', logRocketError);
  }
};

/**
 * Check if LogRocket is properly initialized and working
 */
export const checkLogRocketStatus = (): void => {
  try {
    // Check if LogRocket is available in window
    const isLogRocketAvailable =
      typeof window !== 'undefined' &&
      !!(window as unknown as { LogRocket?: unknown }).LogRocket;

    if (!isLogRocketAvailable) {
      console.warn('LogRocket: Not found in window object');
      return;
    }

    // Check if environment variables are set
    const appId = import.meta.env.VITE_LOGROCKET_APP_ID;
    const serverUrl = import.meta.env.VITE_LOGROCKET_SERVER_URL;

    console.group('🔍 LogRocket Status Check');
    console.log('✅ LogRocket available in window:', !!isLogRocketAvailable);
    console.log('✅ VITE_LOGROCKET_APP_ID set:', !!appId, appId ? `(starts with: ${appId.substring(0, 10)}...)` : '');
    console.log('✅ VITE_LOGROCKET_SERVER_URL set:', !!serverUrl, serverUrl || '');

    // Try to get session URL if available
    if (isLogRocketAvailable) {
      const logRocketInstance = (window as unknown as { LogRocket?: unknown })
        .LogRocket as
        | { getSessionURL?: (cb: (sessionURL: string) => void) => void }
        | undefined;
      if (typeof logRocketInstance?.getSessionURL === 'function') {
        logRocketInstance.getSessionURL((sessionURL: string) => {
          console.log('🎥 Current session URL:', sessionURL);
        });
      }
    }

    console.groupEnd();
  } catch (error) {
    console.error('LogRocket status check failed:', error);
  }
};

/**
 * Send a test event to verify LogRocket is working
 */
export const sendTestEvent = (): void => {
  try {
    const testEventName = 'logrocket_test_event';
    const testProperties = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      test: true,
    };

    trackEvent(testEventName, testProperties);
    console.log('📤 Test event sent to LogRocket:', testEventName, testProperties);
  } catch (error) {
    console.error('Failed to send test event:', error);
  }
};

/**
 * Trigger a test exception to verify error tracking
 */
export const triggerTestException = (): void => {
  try {
    console.log('🧪 Triggering test exception for LogRocket...');
    // Create a test error
    const testError = new Error('LogRocket test exception - this is expected for testing');
    testError.name = 'LogRocketTestError';

    // Add some context
    const context = {
      test: true,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    captureException(testError, context);
    console.log('❌ Test exception sent to LogRocket with context:', context);
  } catch (error) {
    console.error('Failed to trigger test exception:', error);
  }
};
